import chapterImg1 from '../../../../assets/images/courses/team-players/chapter 1.1.jpg';
import chapterImg2 from '../../../../assets/images/courses/team-players/chapter 1.2.jpg';
import introImg from '../../../../assets/images/courses/team-players/title.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { MediaBlock } from '../../MediaBlock';

interface TeamPlayersProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const TeamPlayersChapter1: React.FC<TeamPlayersProps> = ({
  chapter,
  onNextChapter,
  totalPages,
}) => {
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Overview</h2>

        <MediaBlock mediaImage={introImg}>
          <p className="text-xl text-left font-light mt-5 mb-5">
            Teamwork is the ability to work together toward a common vision. The ability to direct
            individual accomplishments toward organizational objectives. It is the fuel that allows
            common people to attain uncommon results."
            <br />
            <br />~ Andrew Carnegie
          </p>
        </MediaBlock>
        <br />
        <h3 className="text-2xl text-purple-600 font-bold mb-1">Introduction</h3>

        <p className="text-2xl text-center mt-7 mb-20 leading-10">
          Welcome to "Team Players: Roles, Responsibilities, and Effective Collaboration." This
          module is designed to deepen your understanding of the dynamics of teamwork and equip you
          with the skills to excel in any team environment.
        </p>

        <p className="text-lg font-semibold text-center mt-7 mb-20 leading-2">
          Throughout this module, you will explore the importance of teams in various aspects of
          life and work, recognize the foundational principles of effective teamwork, and identify
          the different roles within a team.
        </p>
        <div className="md:flex items-center mb-7">
          <p className="flex-1 leading-8">
            <br />
            <br />
            You will assess your strengths and weaknesses, develop strategies to address team gaps,
            and enhance your collaboration skills. By recognizing the characteristics of great team
            players and effective teams—such as shared vision, reliability, a positive attitude,
            appropriate communication, flexibility, and the willingness to go the extra mile—you
            will learn how to contribute more effectively to any team.
          </p>
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
        </div>

        <p className="text-lg font-semibold text-center mt-7 mb-20 leading-9">
          Additionally, you will conduct thorough strengths and weaknesses assessments, apply
          strategies for leveraging your strengths and managing your weaknesses, and utilize tools
          like the Strenergy Matrix to focus on strengths that energize and empower you. This module
          also emphasizes the value of diverse strengths and perspectives within a team, providing
          you with strategies to manage and appreciate the contributions of all team members.
        </p>
        <p className="text-lg font-semibold text-center mt-7 mb-20 leading-9">
          By the end of this module, you will be better equipped to navigate and excel in any
          team-oriented activity, whether in school, sports, or professional settings, ensuring
          collective success and personal growth.
        </p>

        <h3 className="text-2xl text-purple-600 font-bold mb-1">Learning Objectives</h3>
        <p className="text-xl font-light mb-8">
          Upon completion of this lesson, you will be able to:
        </p>
        <ul className="list-disc pl-5 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">
            Understand the importance of teams in various aspects of life and work, and recognize
            the foundational principles of effective teamwork.
          </li>
          <li className="pl-9 mb-7">
            Identify different roles within a team, assess your strengths, and develop strategies to
            address team gaps and enhance collaboration.
          </li>
          <li className="pl-9 mb-7">
            Recognize the characteristics of great team players and effective teams, including
            shared vision, reliability, positive attitude, appropriate communication, flexibility,
            and going the extra mile.
          </li>
          <li className="pl-9 mb-7">
            Conduct strengths and weaknesses assessments to identify key skills and areas for
            improvement, and apply strategies for leveraging strengths and managing weaknesses.
          </li>
          <li className="pl-9 mb-7">
            Utilize the Strenergy Matrix to focus on strengths that energize and empower you, and
            explore ways to grow within your job roles and comfort zones.
          </li>
          <li className="pl-9 mb-7">
            Appreciate the value of diverse strengths and perspectives in a team, and develop
            strategies for managing and leveraging the strengths of all team members for collective
            success.
          </li>
        </ul>
        <h3 className="text-2xl text-purple-600 font-bold mb-1">
          Teams – An Essential Part of Life & Work
        </h3>
        <p className="text-lg font-semibold text-left mt-7 mb-20 leading-9">
          Teamwork is super important in both life and work, helping people achieve more together
          than they could alone. Whether you're working on a school project, playing on a sports
          team, or helping out at home, being able to collaborate with others is key. Teamwork
          allows different ideas to come together, making it easier to solve problems and get things
          done.
        </p>
        <div className="md:flex items-center mb-7">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg2} alt="" />
          </div>
          <p className="flex-1 leading-9">
            <br />
            It also means sharing tasks, so no one has to do everything by themselves. By working
            well with others, you can accomplish great things, feel more connected, and have fun
            along the way. In short, learning to be a good team player is essential for handling
            life’s challenges and making the most of opportunities.
          </p>
        </div>

        <h3 className="text-2xl text-center font-light mt-10 mb-5">
          There are multiple roles involved in teamwork. Let's explore some of these next.{' '}
        </h3>
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Team Roles
        </button>
      </div>
    </>
  );
};
